import React, { useMemo, useState } from 'react';
import { FileDown } from 'lucide-react';
import { DatePickerButton } from '@/shared/components/ui';
import { parseMockDateToIso, extractRowDateIso } from '@/shared/utils/dateUtils';

// Dedicated export modal for System Logs with a functional date picker
export const SystemLogsExportModal = ({ onClose, data }) => {
  const [selectedDateMock, setSelectedDateMock] = useState(null);

  const columns = Array.isArray(data?.columns) ? data.columns.filter(c => c.key !== 'actions') : [];
  const rows = Array.isArray(data?.rows) ? data.rows : [];

  const selectedIso = selectedDateMock ? parseMockDateToIso(selectedDateMock) : '';

  const rowsToExport = useMemo(() => {
    if (!selectedDateMock) return rows;
    const iso = parseMockDateToIso(selectedDateMock);
    if (!iso) return [];
    return rows.filter(r => extractRowDateIso(r) === iso);
  }, [rows, selectedDateMock]);

  const noData = rows.length === 0;
  const fileName = `system_logs_${selectedIso || 'all'}.csv`;

  const handleExport = () => {
    if (!rowsToExport.length) return;
    let keys = [];
    let labels = [];
    if (columns.length) {
      keys = columns.map(c => c.key);
      labels = columns.map(c => c.label || c.key);
    } else if (rowsToExport.length) {
      keys = Object.keys(rowsToExport[0]).filter(k => !k.startsWith('__'));
      labels = [...keys];
    }
    const lines = [labels.join(',')];
    rowsToExport.forEach(r => {
      lines.push(keys.map(k => `"${String(r[k] ?? '').replace(/"/g,'""')}"`).join(','));
    });
    const csv = '\ufeff' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    onClose?.();
  };

  return (
    <div className="w-full max-w-2xl">
      {/* Header */}
      <div className="flex items-start gap-4 px-6 pt-6 pb-4 border-b border-secondary-100">
        <div className="h-11 w-11 rounded-lg bg-primary-600/10 text-primary-600 grid place-items-center ring-1 ring-primary-600/20 shrink-0">
          <FileDown className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-secondary-900 leading-snug">Export System Logs</h2>
          <p className="text-xs text-secondary-600 mt-1">Optionally pick a date. If left empty, all rows are exported.</p>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="sm:w-60">
            <label className="block text-xs font-medium text-secondary-600 mb-1">Date (optional)</label>
            <DatePickerButton
              selectedDate={selectedDateMock}
              onChange={(val) => setSelectedDateMock(val)}
              allowClear={true}
            />
          </div>
        </div>

        {!noData && (
          <div className="text-[11px] text-secondary-500 font-mono bg-secondary-50 border border-secondary-100 rounded px-2 py-1 inline-flex items-center gap-1">
            <span className="opacity-70">File:</span> {fileName}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-secondary-100" />
      <div className="flex justify-end gap-2 px-6 py-5 bg-secondary-50 rounded-b-2xl">
        <button
          onClick={onClose}
          className="inline-flex items-center rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          onClick={handleExport}
          disabled={noData || !rowsToExport.length}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <FileDown className="h-4 w-4 mr-1" /> Export CSV
        </button>
      </div>
    </div>
  );
};

export default SystemLogsExportModal;
